import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import ls from 'local-storage';

import AutoSaveBoomer from './AutoSaveBoomer';
import loader from '../../../images/loader-boomer.png';

import { 
  BOOMER_BODY_HEIGHT,
  BOOMER_FALLBACK_DATA,
  BOOMER_IMG_PATH, 
  BOOMER_ITEM_ORDER,
  BOOMER_ITEMS_BASE,
  BOOMER_ITEMS_TOP,
  BOOMER_MAX_HEIGHT,
  BOOMER_MAX_WIDTH,
  sameBoomerItem,
} from '../../../services/utils/boomer';

const DrawBoomer = ({ badge, boomerData, forceSave, mutation, onBoomerLoaded, setBoomerImage, allItems }) => {
  //return null;

  const [placedBoomer, setPlacedBoomer] = useState({
    imgData: loader,
    drawn: false
  });
  const hiddenCanvas = useRef(null);
  const boomerImage = useRef();
  const callbacks = useRef({ onBoomerLoaded, setBoomerImage });
  callbacks.current = { onBoomerLoaded, setBoomerImage };

  // Compare input data, since the parent creates a new catalog array on each render.
  const drawingInput = JSON.stringify({ boomerData, allItems });

  useEffect(() => {
    let cancelled = false;
    const pendingImages = [];
    const { boomerData: avatar, allItems: catalog } = JSON.parse(drawingInput);
    const data = avatar && avatar.body ? avatar : BOOMER_FALLBACK_DATA;
    const bodyItem = _.find(catalog, item =>
      item.type === 'body' && Number(item.id) === Number(data.body.body_id));

    if (!bodyItem) {
      return undefined;
    }

    const bodyUrl = Array.isArray(bodyItem.url) ? bodyItem.url[0] : bodyItem.url;
    const bodySource = bodyItem.xmlId ?
      `${BOOMER_IMG_PATH}${Number(bodyItem.xmlId)}.png` : bodyUrl;
    const selectedItems = _.sortBy((data.items || []).filter(item => item && item.type !== 'backdrop'),
      item => BOOMER_ITEM_ORDER.indexOf(item.type));
    const layers = [];

    const draw = () => {
      if (cancelled || !hiddenCanvas.current) return;

      const canvas = hiddenCanvas.current;
      const context = canvas.getContext('2d');
      const yPad = ((BOOMER_BODY_HEIGHT - BOOMER_ITEMS_BASE) - BOOMER_ITEMS_TOP) / 2;
      const bodyY = (canvas.height / 2) - (layers[0].img.height / 2) + yPad;
      context.clearRect(0, 0, canvas.width, canvas.height);
      layers.forEach(({ img, iData }) => {
        context.drawImage(img,
          (canvas.width - img.width) / 2 + (Number(iData.xOffset) || 0),
          bodyY + (Number(iData.yPos) || 0), img.width, img.height);
      });
      const imgData = canvas.toDataURL();
      ls('lastBoomerData', JSON.stringify(data));
      setPlacedBoomer({ imgData, drawn: true });
      if (callbacks.current.setBoomerImage) callbacks.current.setBoomerImage(imgData);
    };

    let nextItemIndex = 0;
    const loadNextItem = () => {
      if (cancelled) return;
      const nextItem = selectedItems[nextItemIndex++];
      if (!nextItem) {
        draw();
        return;
      }
      const tile = Number(nextItem.tile || 1);
      const catalogItem = _.find(catalog, item => sameBoomerItem(item, nextItem));
      const urls = nextItem.url || (catalogItem && catalogItem.url);
      const styleUrl = Array.isArray(urls) ? urls[tile - 1] : urls;
      const source = nextItem.xmlId ?
        `${BOOMER_IMG_PATH}${Number(nextItem.xmlId)}-${tile}.png` : styleUrl;
      loadLayer(source, nextItem);
    };

    const loadLayer = (src, iData, isBody = false) => {
      const img = new Image();
      pendingImages.push(img);
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (cancelled) return;
        layers.push({ img, iData });
        loadNextItem();
      };
      img.onerror = () => {
        if (cancelled) return;
        if (isBody) {
          setPlacedBoomer({ imgData: null, drawn: false });
        } else {
          // A missing clothing image must not stop the remaining layers.
          loadNextItem();
        }
      };
      if (src) {
        // Keep canvas requests separate from thumbnails cached without CORS.
        let imageUrl;
        try {
          imageUrl = new URL(src, window.location.href);
        } catch (error) {
          img.onerror();
          return;
        }
        const isRemoteImage = (imageUrl.protocol === 'https:' || imageUrl.protocol === 'http:') &&
          imageUrl.origin !== window.location.origin;
        if (isRemoteImage && !imageUrl.search) {
          imageUrl.searchParams.set('boomer-preview', '1');
        }
        img.src = imageUrl.href;
      } else {
        img.onerror();
      }
    };

    setPlacedBoomer(previous => ({ ...previous, drawn: false }));
    loadLayer(bodySource, data.body, true);
    return () => {
      cancelled = true;
      pendingImages.forEach(img => { img.onload = null; img.onerror = null; });
    };
  }, [drawingInput]);

  useEffect(() => {
    if (placedBoomer.drawn && callbacks.current.onBoomerLoaded && boomerImage.current) {
      callbacks.current.onBoomerLoaded(true);
    }
  }, [placedBoomer]);

  return (
    <div className={`boomer-holder ${mutation ? 'boomer-holder--' + mutation : ''}`}>
      {
        placedBoomer.imgData ? 
          (
            forceSave ? 
            <AutoSaveBoomer imgData={placedBoomer.imgData} /> : 
            <img alt="Your Boomer" className="boomer-holder__image" src={placedBoomer.imgData} ref={boomerImage} />
          )
        :
        <img alt="Your Boomer" className="boomer-holder__loader" src={loader} />
      }
      {badge && placedBoomer.imgData && <img src={badge} alt="badge" className="boomer-holder__badge"/>}
      <canvas
        className="boomer-holder__hidden-canvas"
        height={`${BOOMER_MAX_HEIGHT}`}
        ref={hiddenCanvas}
        width={`${BOOMER_MAX_WIDTH}`}
      />
    </div>
  );
}

export default DrawBoomer;
